import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Match } from "@/mocks/matches";
import TeamBadge from "@/components/base/TeamBadge";

interface HeroBannerProps {
  slides: Match[];
}

export default function HeroBanner({ slides }: HeroBannerProps) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const slide = slides[index];
  const hasScore = slide.status !== "upcoming" && slide.score;

  const go = (dir: number) => setIndex((i) => (i + dir + count) % count);

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.cover}
            alt={`${s.home.name} vs ${s.away.name} 足球赛事直播`}
            title={`${s.home.name} vs ${s.away.name} 直播`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25" />

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-[1280px] mx-auto">
          <div key={slide.id} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-live" />
                正在直播
              </span>
              <span className="px-3 py-1 rounded-full bg-white/15 text-white text-xs backdrop-blur">
                {slide.leagueName} · {slide.round}
              </span>
            </div>

            <div className="flex items-center gap-4 md:gap-6 mb-5">
              <div className="flex flex-col items-center gap-2 w-20 md:w-24">
                <TeamBadge short={slide.home.short} color={slide.home.color} size="lg" />
                <span className="text-white text-sm md:text-base font-medium text-center leading-tight">
                  {slide.home.name}
                </span>
              </div>

              <div className="flex flex-col items-center px-2">
                {hasScore ? (
                  <span className="font-label text-4xl md:text-6xl font-semibold text-white tracking-wider">
                    {slide.score?.home}
                    <span className="text-white/40 mx-2">:</span>
                    {slide.score?.away}
                  </span>
                ) : (
                  <span className="font-label text-3xl md:text-5xl font-semibold text-white">
                    {slide.time}
                  </span>
                )}
                <span className="text-white/70 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-time-line" />
                  {slide.minute ? `进行中 ${slide.minute}'` : slide.date}
                </span>
              </div>

              <div className="flex flex-col items-center gap-2 w-20 md:w-24">
                <TeamBadge short={slide.away.short} color={slide.away.color} size="lg" />
                <span className="text-white text-sm md:text-base font-medium text-center leading-tight">
                  {slide.away.name}
                </span>
              </div>
            </div>

            <p className="text-white/70 text-sm mb-6 flex items-center gap-2">
              <i className="ri-map-pin-line" />
              {slide.venue}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/live"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-live-line text-base" />
                进入直播间
              </Link>
              <Link
                to={`/detail/${slide.id}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-white/15 hover:bg-white/25 text-white text-sm font-medium backdrop-blur border border-white/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line text-base" />
                查看比赛详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10 px-4 md:px-8">
        <div className="w-full max-w-[1280px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到第 ${i + 1} 场赛事`}
                className={`h-1 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-4 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="上一场"
              className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="下一场"
              className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}